import { SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { normalizeProviderForDisplay } from "../ProviderDropdown/utils";

import opentelemetryLogo from "@/assets/integrations/opentelemetry.png";
import promptlayerLogo from "@/assets/logo/promptlayer-cake-color.png";

const PROVIDER_ICON_SLUGS: Record<string, string> = {
  openai: "openai",
  anthropic: "anthropic",
  google: "google",
  "openai.azure": "azure",
  "amazon.bedrock": "bedrock",
  "anthropic.bedrock": "bedrock",
  together: "together",
  anyscale: "anyscale",
  huggingface: "huggingface",
  cohere: "cohere",
  gemini: "gemini",
  mistral: "mistral",
  openrouter: "openrouter",
  fireworks: "fireworks",
  groq: "groq",
  llama: "llama",
  vertexai: "vertexai",
  settings: "settings",
  langchain: "langchain",
  vercel: "vercel",
  pydantic: "pydanticai",
  livekit: "livekit",
  openclaw: "openclaw",
};

const PROVIDER_LOCAL_ICONS: Record<string, string> = {
  promptlayer: promptlayerLogo,
  opentelemetry: opentelemetryLogo,
};

interface ProviderIconProps {
  provider: string;
  className?: string;
  filter?: string;
  colored?: boolean;
}

const getProviderIconSrc = (slug: string, variant: "color" | "brand-color" | "mono") => {
  const base = `https://unpkg.com/@lobehub/icons-static-svg@latest/icons/${slug}`;

  if (variant === "color") {
    return `${base}-color.svg`;
  }

  if (variant === "brand-color") {
    return `${base}-brand-color.svg`;
  }

  return `${base}.svg`;
};

export const ProviderIcon = ({
  provider,
  className = "h-3.5 w-3.5 shrink-0",
  filter,
  colored = false,
}: ProviderIconProps) => {
  const [error, setError] = useState(false);
  const [iconVariant, setIconVariant] = useState<"color" | "brand-color" | "mono">(
    colored ? "color" : "mono",
  );
  const normalizedProvider = useMemo(() => normalizeProviderForDisplay(provider || ""), [provider]);
  const localIconSrc =
    PROVIDER_LOCAL_ICONS[provider || ""] || PROVIDER_LOCAL_ICONS[normalizedProvider];
  const slug = useMemo(
    () =>
      PROVIDER_ICON_SLUGS[provider || ""] ||
      PROVIDER_ICON_SLUGS[normalizedProvider] ||
      normalizedProvider,
    [normalizedProvider, provider],
  );

  useEffect(() => {
    setIconVariant(colored ? "color" : "mono");
    setError(false);
  }, [colored, slug]);

  const handleIconError = () => {
    if (iconVariant === "color") {
      setIconVariant("brand-color");
      return;
    }

    if (iconVariant === "brand-color") {
      setIconVariant("mono");
      return;
    }

    setError(true);
  };

  if (localIconSrc) {
    return (
      <img src={localIconSrc} alt={`${provider} icon`} className={`${className} ${filter ?? ""}`} />
    );
  }

  if (error) {
    return <SlidersHorizontal className={`${className} text-gray-400`} />;
  }

  return (
    <img
      src={getProviderIconSrc(slug, iconVariant)}
      alt={`${provider} icon`}
      className={`${className} ${filter ?? ""}`}
      onError={handleIconError}
    />
  );
};
